import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button, FAIcon, SVGIcon } from '@/components_deprecated'
import { ALL_SALARY_MODULE_CONFIGS_QUERY } from '@/modules/companies'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import type { SalaryModuleConfigQuery } from '@/modules/companies/types.graphql'
import { SalaryGroups } from '@/modules/salary/SalaryGroups'
import { DynamicTableSettings } from '../components'
import { useSalaryModuleConfiguration } from '@/modules/salary/hooks/useSalaryModuleConfiguration'
import { ColumnNames } from '@/modules/dynamic_tables'
import React, { useMemo } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useModuleConfig, useOnErrorAuto } from '@/utils/hooks'
import {
  Header,
  InfoBox,
  OldSection,
  SectionHeader,
  SettingsWrapper,
} from '../components/common'
import { ModuleOptionCards } from '../components/ModuleOptionCards'
import { DepartmentSettings } from '../components/SalarySettings/DepartmentSettings'
import { FacilitySettings } from '../components/SalarySettings/FacilitySettings'
import { HolidayAllowanceSettings } from '../components/SalarySettings/HolidayAllowanceSettings'
import { SalaryCodeSettings } from '../components/SalarySettings/SalaryCodeSettings'
import { SalaryWorkTimesTables } from '../components/SalarySettings/SalaryWorkTimesTables'
import { useSalaryModuleTranslations } from '../hooks'
import { useSalaryMutations } from '../util/mutations.hooks'
const Create = styled.div`
  position: relative;
`

interface SalarySettingsProps {}

export const SalarySettings: React.FC<SalarySettingsProps> = () => {
  const translations = useTranslate({
    info: 'settings.info.salary',
    salarySettings: 'settings.salary-and-work-time',

    defaultSalaryCode: 'settings.default-salary-code',
    moduleOptions: 'settings.module-options',

    module: 'common.module',
    name: 'common.name',
    dailyLimit: 'settings.daily-work-limit-short',
    weeklyLimit: 'settings.period-work-limit-short',
    pauseDuration: 'settings.pause-duration-short',
    pauseThreshold: 'settings.pause-threshold-short',
    salaryPeriod: 'settings.period',
    prompt: {
      deleteTitle: 'settings.prompts.salary-setting-delete-title',
      delete: ['settings.prompts.delete', { name: '' }],
    },

    priority: 'common.priority',

    delete: 'common.delete',
    edit: 'common.edit',
    add: 'common.add',
    departments: 'common.departments',
  })

  const navigate = useNavigate()

  const onErrorAuto = useOnErrorAuto()
  const mutations = useSalaryMutations()
  const { moduleConfig: salaryModuleConfig } = useModuleConfig(Module.SALARY)

  const salaryConfig = useSalaryModuleConfiguration()
  const lockedColumns = useMemo(() => {
    // Map: column name → forced enabled value when locked.
    // Driver/manager: option ON → forced enabled=true (must show)
    // Flex: option OFF → forced enabled=false (must hide)
    const map = new Map<string, boolean>()
    if (salaryConfig.driverApprove)
      map.set(ColumnNames.APPROVED_BY_DRIVER, true)
    if (salaryConfig.managerApprove)
      map.set(ColumnNames.APPROVED_BY_MANAGER, true)
    if (!salaryConfig.canTransferWorkToAbsence)
      map.set(ColumnNames.TRANSFERRED_TO_FLEX, false)
    return map
  }, [
    salaryConfig.driverApprove,
    salaryConfig.managerApprove,
    salaryConfig.canTransferWorkToAbsence,
  ])

  const { data, loading: queryLoading } = useQuery<SalaryModuleConfigQuery>(
    ALL_SALARY_MODULE_CONFIGS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onError: onErrorAuto(),
    }
  )

  const moduleConfigs =
    data?.allSalaryModuleConfigs.edges
      .map(edge => edge.node)
      .filter(node => node.active) ?? []

  const getModuleTranslation = useSalaryModuleTranslations()

  const isLoading = queryLoading || mutations.loading

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox initCollapsed={isMobileOnly}>{translations.info}</InfoBox>

      <SalaryGroups />
      {moduleConfigs.map(config => {
        const salaryWorkTimes = config.salaryWorkTimes
        const icon =
          config.name === 'Activities'
            ? 'truck'
            : config.name === 'Terminal'
            ? 'house'
            : 'clock'
        const iconWidth = config.name === 'Timesheets' ? '20px' : '28px'

        return (
          <OldSection key={config.id}>
            <Header>
              <SectionHeader loading={isLoading}>
                <SVGIcon icon={icon} fill="black" width={iconWidth} />
                {getModuleTranslation(config.name)}
              </SectionHeader>

              <Create>
                {isMobileOnly ? (
                  <Button
                    height="32px"
                    padding="0 1rem"
                    onClick={() =>
                      void navigate('/settings/salary/create', {
                        state: { salaryModule: config.name },
                      })
                    }
                  >
                    <FAIcon icon="plus" $size="0.8rem" />
                  </Button>
                ) : (
                  <Button
                    height="36px"
                    padding="0 2rem"
                    iconLeftProps={{
                      icon: 'plus',
                    }}
                    onClick={() =>
                      void navigate('/settings/salary/create', {
                        state: {
                          salaryModule: config.name,
                        },
                      })
                    }
                  >
                    {translations.add}
                  </Button>
                )}
              </Create>
            </Header>
            <SalaryWorkTimesTables salaryWorkTimes={salaryWorkTimes} />
          </OldSection>
        )
      })}

      <SalaryCodeSettings />
      <DepartmentSettings />
      <FacilitySettings />
      <HolidayAllowanceSettings />

      {!!salaryModuleConfig && (
        <OldSection>
          <Header>
            <SectionHeader>{translations.moduleOptions}</SectionHeader>
          </Header>
          <ModuleOptionCards
            module={Module.SALARY}
            includeOptions={[
              ModuleOptionsValue.SALARY_REQUIRE_COMMENT_ON_DRIVER_APPROVAL,
              ModuleOptionsValue.SALARY_REQUIRE_COMMENT_ON_MANAGER_APPROVAL,
            ]}
          />
        </OldSection>
      )}
      <OldSection>
        <DynamicTableSettings
          name={Module.SALARY}
          minEnabledColumns={0}
          lockedColumns={lockedColumns}
        />
      </OldSection>
    </SettingsWrapper>
  )
}
